import type { TFunction } from 'i18next';

import { getGASessionId, getGAClientId } from '../utils/analytics';

export interface RolePlayData {
  scenario?: string;
  duration?: number;
  difficulty?: string;
  module?: string;
}

export interface RolePlaySettings {
  selections: any;
  componentDetails: any;
}

export interface FeedbackMetadata {
  timestamp: string;
  currentPage: string;
  pathname: string;
  userAgent: string;
  userId: string;
  userRole: string;
  screenshot?: string;
  gaSessionId?: string;
  gaClientId?: string;
  sessionId?: string;
  rolePlayData?: RolePlayData;
  rolePlaySettings?: any;
}

interface MetadataGeneratorConfig {
  currentUser: any;
  location: { pathname: string };
  sessionId?: string;
  rolePlayData?: RolePlayData;
  rolePlaySettings?: RolePlaySettings;
  screenshot?: string | null;
}

// Routes whose label isn't just their first path segment. Ordered: the first
// pattern that matches wins, so a detail route has to precede its list route.
// Where a route's path and its sidebar label disagree, the label wins — a triager
// reading "Feedback" has to land on the page the reporter was actually looking at.
const PAGE_NAME_OVERRIDES: Array<[RegExp, string]> = [
  [/^\/$/, 'Home'],
  [/^\/roleplay(\/|$)/, 'Role Play'],
  [/^\/sessions\/[^/]+/, 'Session Details'],
  // Sidebar "Feedback" (session feedback) lives at /sessions, while /feedback is the
  // admin "App Feedback" page. Labelling /feedback "Feedback" made the two pages
  // indistinguishable in a feedback report.
  [/^\/sessions(\/|$)/, 'Feedback'],
  [/^\/feedback(\/|$)/, 'App Feedback'],
  [/^\/reviewer-dashboard(\/|$)/, 'My Reviews'],
  [/^\/platform-dashboard(\/|$)/, 'Platform Overview'],
];

/**
 * Human-readable page name for a pathname, for feedback triage.
 *
 * This used to iterate an object literal and test `path.includes(key)` against
 * a map whose first entry was `'/': 'Role Play'`. Every path contains a slash,
 * so every page reported itself as "Role Play" — a bug report filed from
 * `/sessions/<id>` was labelled Role Play, and the label was useless. `'/'` is
 * the Home page now anyway; roleplay moved to `/roleplay`.
 *
 * Anything without an override falls back to the title-cased first segment
 * (`/my-progress` → "My Progress", `/prompt-studio/prompts/new` → "Prompt
 * Studio"), so a route added later gets a sensible label without being listed
 * here. The exact pathname is stored alongside this, so the label only has to
 * name the area.
 */
export function resolvePageName(pathname: string): string {
  const override = PAGE_NAME_OVERRIDES.find(([pattern]) => pattern.test(pathname));
  if (override) return override[1];

  const [segment] = pathname.replace(/^\/+/, '').split('/');
  if (!segment) return 'Unknown Page';

  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// resolvePageName's label for each sidebar route → that route's sidebar string. The
// stored page name stays English (it is triage data, and existing reports already hold
// it), so it is translated only where it is shown.
const PAGE_NAME_LABEL_KEYS: Record<string, string> = {
  Home: 'sidebar.home',
  'Role Play': 'sidebar.rolePlay',
  'My Assignments': 'sidebar.myAssignments',
  'My Practice': 'sidebar.myPractice',
  'My Grades': 'sidebar.myGrades',
  'My Classes': 'sidebar.myClasses',
  Feedback: 'sidebar.feedback',
  'My Progress': 'sidebar.myProgress',
  Competitions: 'sidebar.competitions',
  Assignments: 'sidebar.assignments',
  Reviews: 'sidebar.reviews',
  'My Reviews': 'sidebar.myReviews',
  Bundles: 'sidebar.bundles',
  'Team Progress': 'sidebar.teamProgress',
  Users: 'sidebar.users',
  'Join Requests': 'sidebar.joinRequests',
  Classes: 'sidebar.classes',
  Dashboard: 'sidebar.dashboard',
  Characters: 'sidebar.characters',
  'Prompt Studio': 'sidebar.promptStudio',
  'Scene Editor': 'sidebar.sceneEditor',
  Organizations: 'sidebar.organizations',
  'My Organization': 'sidebar.myOrganization',
  'Platform Overview': 'sidebar.platformOverview',
  Reports: 'sidebar.reports',
  'App Feedback': 'sidebar.appFeedback',
  'Unknown Page': 'feedbackManagement.unknownPage',
};

/**
 * A stored page name in the UI language, using the sidebar's wording. Names with no
 * sidebar entry (e.g. "Session Details", or a route added later) are shown as stored.
 */
export function localizePageName(pageName: string, t: TFunction): string {
  const key = PAGE_NAME_LABEL_KEYS[pageName];
  return key ? t(key) : pageName;
}

export class MetadataGenerator {
  private readonly config: MetadataGeneratorConfig;

  constructor(config: MetadataGeneratorConfig) {
    this.config = config;
  }

  // Get current page name from pathname
  private getCurrentPageName(): string {
    return resolvePageName(this.config.location.pathname);
  }

  // Generate comprehensive role play settings metadata
  public getRolePlaySettingsMetadata(): Record<string, string> | null {
    if (!this.config.rolePlaySettings) return null;

    const { selections, componentDetails } = this.config.rolePlaySettings;
    // Only the roleplay page carries these settings. '/' is Home now, and the
    // route is '/roleplay' — the old check also matched '/role-plays', the
    // admin list page, which has no such settings.
    if (this.getCurrentPageName() !== 'Role Play') return null;

    return this.buildRolePlayMetadata(selections, componentDetails);
  }

  private buildRolePlayMetadata(selections: any, componentDetails: any): Record<string, string> {
    const getValueOrDefault = (value: any): string => value || 'none';

    return {
      // Selection IDs and basic info
      module: getValueOrDefault(componentDetails?.base_config?.name || componentDetails?.module?.name),
      student_profile: getValueOrDefault(componentDetails?.student_profile?.name),
      scenarios: getValueOrDefault(componentDetails?.scenarios?.name),
      preferred_name: getValueOrDefault(selections?.preferred_name),
      notes: getValueOrDefault(selections?.notes),

      // Student profile details
      student_age: getValueOrDefault(componentDetails?.student_profile?.age),
      student_gender: getValueOrDefault(componentDetails?.student_profile?.gender),
      student_background: getValueOrDefault(componentDetails?.student_profile?.background),

      // Avatar and interaction settings
      avatar_type: getValueOrDefault(selections?.avatar_type || componentDetails?.student_profile?.avatar_type),
      ai_voice: getValueOrDefault(componentDetails?.student_profile?.ai_voice),

      // Session settings
      session_duration: getValueOrDefault(componentDetails?.base_config?.session_duration || componentDetails?.module?.session_duration),

      // Module/scenario details
      module_description: getValueOrDefault(componentDetails?.base_config?.description || componentDetails?.module?.description),
      scenario_description: getValueOrDefault(componentDetails?.scenarios?.description),

      // Teaching/learning details
      skills: this.arrayToString(componentDetails?.scenarios?.skills),
      examples: this.arrayToString(componentDetails?.scenarios?.examples),

      // Corrective teaching steps if available
      teaching_steps: componentDetails?.scenarios?.steps 
        ? Object.keys(componentDetails.scenarios.steps).join(', ') 
        : 'none'
    };
  }

  private arrayToString(array: any[]): string {
    return array && Array.isArray(array) ? array.join(', ') : 'none';
  }

  // Calculate the actual number of metadata items that will be displayed
  public getMetadataItemCount(): number {
    let count = 2; // Always have: Current Page + Timestamp

    // Screenshot
    if (this.config.screenshot) count++;

    // GA tracking items (we'll count them optimistically)
    count += 2; // gaSessionId and gaClientId

    // Session ID
    if (this.config.sessionId) count++;

    // Role play settings (count each individual setting)
    const rolePlaySettingsData = this.getRolePlaySettingsMetadata();
    if (rolePlaySettingsData) {
      count += Object.keys(rolePlaySettingsData).length;
    }

    // Legacy role play data (if present and no modern role play settings)
    if (this.config.rolePlayData && !rolePlaySettingsData) count++;

    return count;
  }

  // Generate metadata object
  public async generateMetadata(): Promise<FeedbackMetadata> {
    const baseMetadata: FeedbackMetadata = {
      timestamp: new Date().toISOString(),
      currentPage: this.getCurrentPageName(),
      pathname: this.config.location.pathname,
      userAgent: navigator.userAgent,
      userId: this.config.currentUser?.id || 'anonymous',
      userRole: this.config.currentUser?.role || 'unknown'
    };

    // Add optional fields
    if (this.config.screenshot) {
      baseMetadata.screenshot = this.config.screenshot;
    }

    if (this.config.sessionId) {
      baseMetadata.sessionId = this.config.sessionId;
    }

    if (this.config.rolePlayData) {
      baseMetadata.rolePlayData = this.config.rolePlayData;
    }

    // Add comprehensive role play settings
    const rolePlaySettingsData = this.getRolePlaySettingsMetadata();
    if (rolePlaySettingsData) {
      baseMetadata.rolePlaySettings = rolePlaySettingsData;
    }

    // Add Google Analytics tracking information
    try {
      const [gaSessionId, gaClientId] = await Promise.all([
        getGASessionId(),
        getGAClientId()
      ]);

      if (gaSessionId) {
        baseMetadata.gaSessionId = gaSessionId;
      }

      if (gaClientId) {
        baseMetadata.gaClientId = gaClientId;
      }
    } catch (error) {
      console.warn('Failed to load GA tracking data:', error);
    }

    return baseMetadata;
  }
}

// Factory function for easier usage
export const createMetadataGenerator = (config: MetadataGeneratorConfig): MetadataGenerator => {
  return new MetadataGenerator(config);
};